import React, { useCallback, useEffect, useRef, useState } from 'react'
import { renderToStaticMarkup } from 'react-dom/server'
import { debounce, platformTransitionStyle } from '../../../../shared/helpers'
import {
  KeyTypeEnum,
  useKeyDownHandler,
} from '../../../../shared/hooks/useKeyDownHandler'
import googleAnalytics4 from '../../lib/googleAnalytics.js'
import tts from '../../lib/TTS'
import { createPortal } from 'react-dom'
import { useSidePanel } from '../../components/SidePanel/SidePanelProvider'

import type { SettingsScreenModalProps } from './types'

const SCROLL_DEBOUNCE_MS = 50
const SCROLL_INTERVAL = 100

const SettingsScreenModal = ({
  content,
  onClose,
  title,
  visible = false,
}: SettingsScreenModalProps) => {
  const contentRef = useRef<HTMLDivElement>(null)
  const [hasOverflow, setHasOverflow] = useState(false)
  const { setKeepLogoVisible } = useSidePanel()

  const scrollContent = useCallback((direction: 'UP' | 'DOWN') => {
    const container = contentRef.current
    if (!container) return

    const offset =
      direction === 'DOWN'
        ? container.scrollTop + SCROLL_INTERVAL
        : container.scrollTop - SCROLL_INTERVAL

    container.scrollTop = offset
  }, [])

  const debouncedScroll = useCallback(
    debounce(scrollContent, SCROLL_DEBOUNCE_MS),
    [scrollContent]
  )

  useKeyDownHandler(
    [KeyTypeEnum.Back, KeyTypeEnum.Down, KeyTypeEnum.Up],
    (key) => {
      switch (key) {
        case KeyTypeEnum.Back:
          return onClose()
        case KeyTypeEnum.Down:
          return debouncedScroll('DOWN')
        case KeyTypeEnum.Up:
          return debouncedScroll('UP')
      }
    },
    visible
  )

  useEffect(() => {
    setKeepLogoVisible(true)
    return () => {
      setKeepLogoVisible(false)
    }
  }, [])

  useEffect(() => {
    const container = contentRef.current

    if (container) {
      setHasOverflow(container.scrollHeight > container.clientHeight)
    }
    googleAnalytics4.sendPageViewEvent(title)
  }, [title])

  useEffect(() => {
    if (!visible && contentRef.current) {
      contentRef.current.scrollTop = 0
    }
  }, [visible])

  useEffect(() => {
    if (visible && content) {
      const contentText =
        typeof content === 'string'
          ? content
          : renderToStaticMarkup(<>{content}</>)
              .replace(/<[^>]+>/g, ' ')
              .replace(/\s+/g, ' ')
              .trim()
      const textToRead = `${title}. ${contentText}`

      const timeout = setTimeout(() => {
        tts.addTextToRead(textToRead)
      }, 300)

      return () => {
        clearTimeout(timeout)
        tts.stopRead()
      }
    } else {
      tts.stopRead()
    }
  }, [visible, content, title])

  if (!visible) return null

  return createPortal(
    <div className="settings-scrolling-text-modal">
      <h1 className="settings-scrolling-text-title">{title}</h1>
      <div className="scroll-hide">
        <div
          className="content"
          ref={contentRef}
          style={{
            transition: platformTransitionStyle('transform 500ms ease-in-out'),
            overflowY: hasOverflow ? 'auto' : 'hidden',
          }}
        >
          {content}
        </div>
      </div>
    </div>,
    document.getElementById('full-width-background')!
  )
}

export default SettingsScreenModal
